import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pluggyVinculosApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input, Toggle } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { LoadingState, ErrorState } from '../../components/ui/EmptyState'
import { useToast } from '../../components/ui/Toast'
import { formatDate, formatMoney } from '../../lib/utils'
import { invalidarPluggy } from './invalidar'

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-13">
      <span className="text-muted">{rotulo}</span>
      <b className="text-right text-white">{children}</b>
    </div>
  )
}

/** "Refazer configuração": apaga o que veio do banco (e, se pedido, o digitado) e volta o vínculo para Pendente. */
export function RefazerConfiguracao({ vinculoId, nomeAlvo, onClose, onRefeito }: {
  vinculoId: string
  nomeAlvo?: string
  onClose: () => void
  /** Chamado ao concluir; o chamador abre o assistente do mesmo vínculo. */
  onRefeito: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [apagarManuais, setApagarManuais] = useState(false)
  const [nome, setNome] = useState('')

  const resumoQ = useQuery({
    queryKey: ['pluggy-refazer-resumo', vinculoId, apagarManuais],
    queryFn: () => pluggyVinculosApi.resumoRefazer(vinculoId, apagarManuais),
    placeholderData: (anterior) => anterior,
    staleTime: 0,
  })
  const resumo = resumoQ.data

  const refazer = useMutation({
    mutationFn: () => pluggyVinculosApi.refazerVinculo(vinculoId, { apagarManuais, confirmacao: apagarManuais ? nome : undefined }),
    onSuccess: (r) => {
      invalidarPluggy(qc)
      qc.invalidateQueries({ queryKey: ['pluggy-configuracao'] })
      toast.success(`Configuração limpa: ${r.lancamentosApagados + r.manuaisApagados} lançamento(s) e ${r.pendenciasApagadas} pendência(s) apagados.`)
      onRefeito()
    },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível refazer a configuração.'),
  })

  const nomeOk = !apagarManuais || (!!resumo && nome === resumo.nomeConfirmacao)
  const protegidos = resumo?.protegidosPorFaturaPaga ?? []

  return (
    <Modal
      open
      onClose={refazer.isPending ? () => {} : onClose}
      size="lg"
      title={`Refazer configuração${nomeAlvo ? ` · ${nomeAlvo}` : ''}`}
      footer={
        <>
          <Button variant="light" onClick={onClose} disabled={refazer.isPending}>Cancelar</Button>
          <Button
            variant={apagarManuais ? 'danger' : 'primary'}
            icon="ri-restart-line"
            loading={refazer.isPending}
            disabled={!resumo || !nomeOk}
            onClick={() => refazer.mutate()}
          >
            {apagarManuais ? 'Apagar tudo da conta e refazer' : 'Apagar o que veio do banco e refazer'}
          </Button>
        </>
      }
    >
      {resumoQ.isError ? (
        <ErrorState message="Não foi possível calcular o que será apagado." />
      ) : !resumo ? (
        <LoadingState />
      ) : (
        <div className="space-y-4">
          <p className="text-13 text-muted">
            Use quando a importação deu errado. Por padrão, só o que veio do banco é apagado: o que você lançou à mão fica protegido. Depois você escolhe o corte e o saldo de novo.
          </p>
          <div className={`space-y-2 rounded border border-border/70 bg-surface2/40 p-4 ${resumoQ.isFetching ? 'opacity-60' : ''}`}>
            <p className="text-15 font-medium text-white">O que será apagado</p>
            <Linha rotulo="Lançamentos que vieram do banco">
              {resumo.lancamentosPluggy}{resumo.periodo ? ` (${formatDate(resumo.periodo.de)} a ${formatDate(resumo.periodo.ate)})` : ''}
            </Linha>
            <Linha rotulo="Pendências da caixa de entrada">{resumo.pendencias}</Linha>
            <Linha rotulo="Seus lançamentos digitados">
              <span className={apagarManuais ? 'text-expense' : 'text-income'}>
                {resumo.manuais} {apagarManuais ? (resumo.manuais === 1 ? 'será apagado' : 'serão apagados') : (resumo.manuais === 1 ? 'fica mantido' : 'ficam mantidos')}
              </span>
            </Linha>
            <Linha rotulo="Protegidos (fatura de cartão paga)">{protegidos.length}</Linha>
            {resumo.saldoDepois != null && <Linha rotulo="Saldo depois de apagar">{formatMoney(resumo.saldoDepois)} (até refazer o corte)</Linha>}
          </div>

          <div className={`space-y-3 rounded border p-4 ${apagarManuais ? 'border-expense' : 'border-border/70'}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-13 font-semibold text-white">Apagar também os lançamentos que eu digitei</p>
                <p className="text-2xs text-muted">Zera a conta inteira. Desligado por padrão.</p>
              </div>
              <Toggle checked={apagarManuais} onChange={(v) => { setApagarManuais(v); setNome('') }} label="" disabled={refazer.isPending} />
            </div>
            {apagarManuais && (
              <Input
                label={`Para confirmar, digite o nome: ${resumo.nomeConfirmacao}`}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome da conta"
                error={nome !== '' && nome !== resumo.nomeConfirmacao ? 'O nome precisa ser igual ao mostrado acima.' : undefined}
                autoComplete="off"
              />
            )}
          </div>

          {protegidos.length > 0 && (
            <div className="rounded border border-border/70 bg-surface2/40 p-3 text-13">
              <p className="mb-2 font-medium text-white">Compras protegidas (fatura já paga), que não serão apagadas</p>
              <ul className="max-h-40 space-y-1 overflow-y-auto">
                {protegidos.map((p) => (
                  <li key={p.id} className="flex justify-between gap-3 text-muted">
                    <span className="truncate">{formatDate(p.data)} · {p.descricao}</span>
                    <span className="whitespace-nowrap text-white">{formatMoney(p.valor)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="rounded border border-warning/50 bg-warning/10 p-3 text-13 text-warning">
            Não dá para desfazer. Compras de cartão em fatura já paga ficam protegidas e não são apagadas.
          </p>
        </div>
      )}
    </Modal>
  )
}
